import { Fragment, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";

import { ScreenSpinner } from "@/components";
import { CareplanContext } from "@/domains/careplan/contexts/use-careplan-context";
import { useMarkCareplanAsRead } from "@/domains/careplan/mutations/use-mark-careplan-as-read";
import { useCareplan } from "@/domains/careplan/queries/use-careplan";
import { useMessages } from "@/domains/messages/queries/use-messages";
import { useProfile } from "@/domains/profile/queries/use-profile";
import { ProgressTeaserCard } from "@/domains/progress/components";
import { selectCareplanProgressTeaserBody } from "@/domains/progress/utils/progress-entry-points";
import { Banner } from "@/routes/_private/(with-footer)/careplan/-components/banner";
import { PreviousCareplanProduct } from "@/routes/_private/(with-footer)/careplan/-components/previous-careplan-product";
import { isCartProcessing, useCartStore } from "@/stores";
import { Button, icons } from "@/ui";
import {
  CareplanProduct,
  CareplanSummaryCard,
  ImageLinkList,
  OngoingSupportCard,
  StickyCheckoutBar,
  YourCareplanHeader,
  YourCareplanHeaderMobile,
} from "./-components";

const Careplan = () => {
  const { openCart } = useCartStore();
  const isProcessing = useCartStore(isCartProcessing);
  const [firstProductCard, setFirstProductCard] = useState<HTMLElement | null>(null);
  const { data: profile } = useProfile();
  const careplanQuery = useCareplan();
  const messagesQuery = useMessages();

  useMarkCareplanAsRead({
    isRead: careplanQuery.careplan?.isRead,
    careplanId: careplanQuery.careplan?.id,
  });

  const progressTeaserBody = selectCareplanProgressTeaserBody(careplanQuery.careplan);

  /*
    Refetching a query that never held data resets its status to pending and clears its error, so
    `isError` drops back to the spinner on every remount and window focus. `errorUpdatedAt` is the
    one failure marker TanStack keeps across that reset.
  */
  const hasCareplanFailed = careplanQuery.errorUpdatedAt > 0 && !careplanQuery.careplan;

  if (!profile || (careplanQuery.isLoading && !hasCareplanFailed) || !messagesQuery.data) {
    return <ScreenSpinner />;
  }

  const careplan = careplanQuery.careplan;

  /*
    The order tools only make sense before a subscription exists, same as the cart's Check Out,
    and not in the minute after checkout while the subscription is being created.
  */
  const showOrderTools = !!careplan && !careplan.subscription && !isProcessing;

  const content = (
    <>
      <YourCareplanHeaderMobile
        className="mb-6 sm:hidden"
        latestMessage={{
          message: messagesQuery.data.latestSystemMessage,
          truncate: !hasCareplanFailed,
        }}
        title={
          hasCareplanFailed ? (
            "Our Clinical Team Is Reviewing Your Plan"
          ) : (
            <>
              <div className="font-extrabold">{`${profile.firstName}'s`}</div>
              <div className="font-medium">Care Plan</div>
            </>
          )
        }
      />

      <div className="flex w-full max-w-4xl grow flex-col gap-6 self-center px-4 pt-0 pb-16 sm:gap-8 sm:px-6 sm:pt-16">
        <YourCareplanHeader
          className="hidden sm:flex"
          latestMessage={{
            message: messagesQuery.data.latestSystemMessage,
            truncate: !hasCareplanFailed,
          }}
          title={
            hasCareplanFailed
              ? "Our Clinical Team Is Reviewing Your Plan"
              : `${profile.firstName}'s Care Plan`
          }
        />

        {progressTeaserBody === null ? null : <ProgressTeaserCard body={progressTeaserBody} />}

        {showOrderTools ? <CareplanSummaryCard detailsElement={firstProductCard} /> : null}

        {careplan
          ? careplan.productsWithHistory.map((product, index) => {
              const isFirstProduct = index === 0;
              /* The first card cues the sticky bar and is where the summary's details button scrolls to. */
              const cardRef = isFirstProduct ? setFirstProductCard : undefined;

              return (
                <Fragment key={product.id}>
                  {product.isPrevious ? (
                    <PreviousCareplanProduct product={product} ref={cardRef} />
                  ) : (
                    <CareplanProduct product={product} ref={cardRef} />
                  )}
                  {isFirstProduct ? (
                    <Banner
                      icon={icons.HydrateIcon}
                      text="Hydrate well for gentler, consistent cannabinoid effects."
                    />
                  ) : null}
                </Fragment>
              );
            })
          : null}

        {!hasCareplanFailed && <OngoingSupportCard />}
        <Banner text="While cannabinoids often provide substantial and immediate relief, best results are typically achieved after 60-90 days of guided use." />
        {!!careplan && (
          <Button className="w-full" onClick={openCart} size="lg">
            <span>View Cart & Check Out</span>
            <icons.ArrowRight className="size-5" />
          </Button>
        )}
        <ImageLinkList />
      </div>

      {/* Phone only; it appears once the top quarter of the first product card is on screen. */}
      {showOrderTools ? <StickyCheckoutBar revealOnce={firstProductCard} /> : null}
    </>
  );

  if (!careplan) {
    return content;
  }

  return <CareplanContext.Provider value={careplan}>{content}</CareplanContext.Provider>;
};

export const Route = createFileRoute("/_private/(with-footer)/careplan/")({
  component: Careplan,
});
